import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  buildAutoTaggingPlansQuery,
  fetchAutoTaggingPlans,
  type AutoTaggingPlanRecord,
  type AutoTaggingPlanTypeOption,
} from "../services/autoTaggingService";
import { mapTaggingPlanRow } from "../pages/AiPlanPage/TaggingPlan/mapTaggingPlanRow";
import type { TaggingPlanRow } from "../pages/AiPlanPage/constants";

export interface UseAutoTaggingPlansOptions {
  objectType?: string;
  objectSubType?: string;
  concept?: string;
  planTypes?: AutoTaggingPlanTypeOption[];
  /** 1-based page number for the API. */
  page?: number;
  pageSize?: number;
}

export interface UseAutoTaggingPlansResult {
  rows: TaggingPlanRow[];
  total: number;
  loading: boolean;
  /** True after the first fetch attempt finishes (success or error). */
  hasLoaded: boolean;
  reload: () => void;
}

export function useAutoTaggingPlans(
  options: UseAutoTaggingPlansOptions,
): UseAutoTaggingPlansResult {
  const [plans, setPlans] = useState<AutoTaggingPlanRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const planTypes = options.planTypes ?? [];

  const query = useMemo(
    () =>
      buildAutoTaggingPlansQuery({
        objectType: options.objectType,
        objectSubType: options.objectSubType,
        concept: options.concept,
        page: options.page ?? 1,
        pageSize: options.pageSize ?? AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
      }),
    [
      options.objectType,
      options.objectSubType,
      options.concept,
      options.page,
      options.pageSize,
    ],
  );

  const reload = useCallback(() => {
    let mounted = true;
    setLoading(true);

    fetchAutoTaggingPlans(query)
      .then((result) => {
        if (!mounted) return;
        setPlans(result.items);
        setTotal(result.total);
      })
      .catch(() => {
        if (!mounted) return;
        setPlans([]);
        setTotal(0);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setHasLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [query]);

  useEffect(() => {
    const cleanup = reload();
    return cleanup;
  }, [reload]);

  const rows = useMemo(
    () => plans.map((plan) => mapTaggingPlanRow(plan, planTypes)),
    [plans, planTypes],
  );

  return {
    rows,
    total,
    loading,
    hasLoaded,
    reload,
  };
}
